import { UnitConverter } from "@vibialighting/vibia-utils";
import { hexDictionary } from "../data/Constants";
import Article from "./Article";
import { i18nLabelInterface, variationType } from "./catalog";
import Price from "./Price";
import { PendantHeightConstraintInterface, VariationProps } from "./variation";
import Toggles from "../toggles/Toggles";
import { convertTimeTo, protectFromUndefinedOrNaN } from "@domain/utils/utils";
import { pendantHeightToCm } from '../utils/utils'

const deepCopy = obj => JSON.parse(JSON.stringify(obj))

const types = {
    Led: 'temperature',
    Dim: 'control',
    Aca: 'color',
    Ins: 'installation',
    Lon: 'pendantHeight'
}

function hexToDecimal(hex: string): number {
    // Parse el valor hexadecimal en decimal usando el método parseInt con base 16
    const decimal = parseInt(hex, 16);
    return decimal;
}

const colorIds = {
    '04': 71,
    '10': 196,
    '11': 198,
    '18': 126,
    '24': 203,
    '37': 209,
    '42': 212,
    '47': 214,
    '93': 241,
    '22': 126, // Dark antracita we don't have this ID. We use Graphite
    //Paleta vibia
    '13': 197,
    '15': 199,
    '23': 23, // Beige L1 has id 23
    '27': 205,
    '36': 208,
    '40': 211,
    '16': 200,
    '28': 206,
    '53': 215,
    '61': 217,
    '21': 201,
    '25': 204,
    '29': 207,
    '38': 210,
    '45': 213,
    '56': 216,
    '68': 68, // Ocre has id 68
    '82': 231,
    '83': 232
}

const temperaturesIds = {
    '1': 1,
    '2': 9,
    '3': 19,
    '4': 20
}

const getModifierFromReference = (referenceBase, type) => {
    let referenceType = 'normal'
    const dashedReference = referenceBase.includes('-')
    if (dashedReference)
        referenceType = 'dashed'
    if (referenceBase.trim().length == 6)
        referenceType = 'digits6'

    const positions = {
        dashed: {
            color: 5,
            control: 9,
            temperature: 8
        },
        normal: {
            color: 4,
            control: 8,
            temperature: 7
        },
        digits6: {
            color: 4
        }
    }
    const position = positions[referenceType]
    const lengths = {
        color: 2,
        control: 1,
        temperature: 1
    }
    return referenceBase.slice(position[type], position[type] + lengths[type])
}

const parseERPModifier = (row) => {
    let modifier
    const type = types[row.TipVariacion]
    const isReference6Digits = row.Articulo?.trim().length == 6
    if (isReference6Digits && type != 'color') { // 6 digits except color
        modifier = row.Keycus1
    } else if (row.BOV == 'B' && type != 'installation') { // Base & !installation
        modifier = getModifierFromReference(row.Articulo, types[row.TipVariacion])
    } else if (type == 'color') { // Variation & type color
        modifier = row.Keycus1.slice(-2)
    } else { // (Variation & !color) || (Base & installation)
        modifier = row.Keycus1
    }
    const fallBackModifier = ['color', 'installation'].includes(type) ? '00' : '0' // Installation & color have 2 digits.
    return modifier.trim() || fallBackModifier // No modifier in ERP -> use fallback modifier in domain
}

const getVariationId = (type, modifier, finishMasters?: Array<{ id: number; code: number }>) => {
    if (type == 'color') {
        const modifierWithoutLeadingZeros = modifier.slice(modifier.length - 2, modifier.length)
        if (Toggles.isEnabled('finishMastersFromAPI') && finishMasters?.length) {
            const idFromMasters = finishMasters.find(
                (f) => String(f.code).padStart(2, '0') === modifierWithoutLeadingZeros
            )?.id
            return idFromMasters ?? colorIds[modifierWithoutLeadingZeros] ?? -1
        }
        return colorIds[modifierWithoutLeadingZeros] ?? -1
    }


    if (type == 'temperature') {
        const temperatureId = temperaturesIds[modifier]
        if (temperatureId) {
            return temperatureId
        } else {
            const decimalModifier = hexToDecimal(modifier)
            const fakeId = 1000 + decimalModifier
            return fakeId
        }
    }


    // Temporary fix while CRM is not available
    let fakeId = parseInt(modifier)
    if (Number.isNaN(fakeId)) {
        let i = 0;
        fakeId = 0;
        while (i < modifier.length) {
            fakeId += modifier.charCodeAt(i);
            i++;
        }
    }
    return fakeId
}

const parseRow = row => {
    const parsed = JSON.parse(JSON.stringify(row))
    for (const key of Object.keys(parsed)) {
        if (typeof parsed[key] == 'string') {
            parsed[key] = parsed[key].trim()
        }
    }
    return parsed
}

const transformDescription = (Descustom, TipVariacion) => {
    switch (TipVariacion) {
        case 'Aca':
            return Descustom.slice(3);
        case 'Led':
            const descustomArray = Descustom.split('');
            let result = descustomArray
            if (!Toggles.isEnabled("deleteSpaceTemperatureDescription") || result[result.length - 1] === "K") {
                result.splice(4, 0, ' ')
            }
            return result.join("")
        default:
            return Descustom
    }
}
export default class Variation {
    props;

    constructor(props: VariationProps) {
        this.props = props
    }

    getProps() {
        return deepCopy(this.props)
    }

    getType(): variationType {
        return this.props.type
    }

    isType(type: variationType) {
        return this.getType() == type
    }

    getValue() {
        return this.getProps().value
    }

    // Gets a specific pendant height variation, coming from a generic pendant height variation
    getSpecificPendantHeightVariation(height) {
        if (this.getType() != 'pendantHeight') {
            throw new Error(`getSpecificPendantHeightVariation only works when applied to 'pendantHeight' variations, not with '${this.getType()} `)
        }

        const value = this.getValue()
        const delta = height - value.standard
        return new Variation({
            ...this.getProps(),
            value: {
                ...value,
                delta
            }
        })
    }

    getLabel(withModifier: boolean = true) { /* TODO (?) -> Añadir también un identificador al resto de atributos que no son color */
        if (withModifier) {
            return `${this.getModifier(true)} ${this.getProps().description.en}`
        } else {
            if (this.getType() == 'color') {
                return `${this.getModifier()} ${this.getProps().description.en}`
            } else {
                return this.props.description.en
            }
        }
    }

    getDescriptions(): i18nLabelInterface {
        return this.getProps().description
    }

    setDescriptions(description: i18nLabelInterface) {
        this.props.description = description
    }

    getBOV() {
        if (this.getType() == 'pendantHeight') {
            const value = this.getValue()
            if (!value?.delta) {
                return 'B' // Pendantheight variation without delta has BOV of B.
            }
        }

        return this.getProps().bov ?? this.getProps().BOV // Should be saved in props.bov
    }

    isBase() {
        return this.getBOV() == 'B'
    }

    isOptional() {
        return this.getBOV() == 'O'
    }

    isVariation() {
        return this.getBOV() == 'V'
    }

    isTemporal() {
        return this.getBOV() == 'T'
    }

    getModel() {
        return this.getProps().model
    }

    getModifier(onlyModifier?: boolean) {
        switch (this.getType()) {
            case 'installation':
                const prefix = onlyModifier ? '' : '_'
                const modifier = prefix + this.props.modifier + this.props.modifier2
                return typeof modifier == 'string' ? modifier.trim() : modifier
            default:
                return typeof this.props.modifier == 'string' ? this.props.modifier.trim() : this.props.modifier
        }
    }

    getRal() {
        return this.props.ral
    }

    setRal(newRal) {
        this.props.ral = newRal
    }

    getFinishThumbnail() {
        return this.props.thumbnail
    }
    setFinishThumbnail(finishImg) {
        this.props.thumbnail = finishImg
    }

    getMOQ(): number {
        return parseFloat(this.getProps().moq)
    }

    getReference(referenceBase = this.getProps().referenceBase) {
        const installationNotVariated = this.isType('installation') && this.isBase()
        const modifier = this.getModifier()
        // Don't apply base installation nor fallback modifiers to reference.
        // Exception: control accepts '0' as a legitimate modifier (On/Off).
        if (installationNotVariated || (modifier == '0' && !this.isType('control')) || modifier == '00')
            return referenceBase

        let reference = referenceBase
        const dashedReference = reference.includes('-')
        let positions = dashedReference ? {
            color: 5,
            control: 9,
            installation: 10,
            temperature: 8
        } : {
            color: 4,
            control: 8,
            installation: 9,
            temperature: 7
        }

        const referenceArr = reference.split('')
        const position = positions[this.getType()]
        if (position) {
            referenceArr.splice(position, modifier.length, modifier)
        }
        return referenceArr.join('')
    }

    getLeadTime(inputLeadTime = []) {
        const variationLeadTime = this.getProps()?.leadtime || []
        // leadtimeA = itemA.leadtime.map(l => {...l,from: Math.ceil(l.from / itemA.quantity)})
        // leadtimeB = itemB.leadtime.map(l => {...l,from: Math.ceil(l.from / itemB.quantity)})
        if (variationLeadTime.length != 0 && inputLeadTime.length != 0) {
            let maxLeadTime = [...inputLeadTime];
            for (const range of variationLeadTime) {
                if (!maxLeadTime.find(maxRange => maxRange.from == range.from)) {
                    maxLeadTime.push(range)
                    continue
                } //Al mergear la segunda array, comprobramos que con mismo value de from, solo pusheemos el objeto con un value de weeks mas
                if (maxLeadTime.find(maxRange => maxRange.from == range.from)) {
                    let indexVar: number = variationLeadTime.findIndex(ran => ran.from == range.from)
                    let indexMax: number = maxLeadTime.findIndex(ran => ran.from == range.from)
                    let maxWeek = maxLeadTime[indexMax].weeks
                    if (variationLeadTime[indexVar].weeks > maxWeek) {
                        maxLeadTime[indexMax].weeks = range.weeks
                    }
                }
            }
            maxLeadTime.sort((a, b) => a.from - b.from);
            for (let i = 1; i < maxLeadTime.length; i++) {
                if (maxLeadTime[i].weeks <= maxLeadTime[i - 1].weeks) {
                    maxLeadTime.splice(i, 1)
                }
            }
            return maxLeadTime;
        } else if (variationLeadTime.length == 0) {
            return inputLeadTime
        } else {
            return variationLeadTime
        }
    }

    /* TIENE AFECTACIÓN EN EL DESENROLLADOR */
    getArticle(inputArticle: Article) {
        if (!inputArticle) {
            return null
        }

        const inputAttributes = inputArticle.getProps().attributes || {}
        const variatedAttributes = JSON.parse(JSON.stringify(inputAttributes))
        const keys = {
            color: 'finish',
            temperature: 'ledColourTemp',
            control: 'control',
            installation: 'installation',
            pendantHeight: 'pendantHeight'
        }

        const code = this.getModifier().replace(/[^a-z0-9]/gi, '')
        const alwaysApplyVariations = Toggles.isEnabled("masterControlsAPI2it")
        const attribute = keys[this.getType()]
        //TODO: I think we can apply the base variation already
        // Dont apply base variation to base article because codes and descriptions from ERP dont match Mongo's (source:CRM)
        if (alwaysApplyVariations || variatedAttributes[attribute]?.code != code) {
            variatedAttributes[attribute] = {
                ...variatedAttributes[attribute] ?? {},
                description: this.getProps().description,
                code,
                id: this.getProps().id,
                bov: this.getBOV(),
            }

            switch (this.getType()) {
                case 'temperature':
                    variatedAttributes[attribute] = {
                        ...variatedAttributes[attribute],
                        value: this.getProps().value
                    }
                    break;
                case 'color':
                    variatedAttributes[attribute] = {
                        ...variatedAttributes[attribute],
                        ral: this.getRal(),
                        thumbnail: this.getFinishThumbnail()
                    }
                    break;
                case 'pendantHeight':
                    const value = this.getValue()
                    let pendantHeight = value.standard + (value.delta ?? 0)
                    // Whole centimetres on the metric catalogs, the exact 3/8 in grid on the US
                    // one — see `pendantHeightToCm` (DV-6315).
                    const heightCatalogId = this.getProps().catalog?.id ?? this.getProps().catalog
                    let standard = UnitConverter.toObjectLengthByCatalog(
                        heightCatalogId,
                        pendantHeightToCm(value.standard, heightCatalogId),
                        UnitConverter.UD_TYPES.CENTIMETERS
                    )
                    variatedAttributes[attribute] = {
                        ...variatedAttributes[attribute],
                        ...UnitConverter.toObjectLengthByCatalog(
                            heightCatalogId,
                            pendantHeightToCm(pendantHeight, heightCatalogId),
                            UnitConverter.UD_TYPES.CENTIMETERS
                        ),
                        standard
                    }
            }
        }

        const articleMoq = inputArticle.getProps().moq
        const variationMoq = this.getProps().moq
        return new Article({
            ...inputArticle.getProps(),
            reference: this.getReference(inputArticle.getReference()),
            referenceBase: this.getProps().referenceBase,
            attributes: variatedAttributes,
            leadtime: this.getLeadTime(inputArticle.getProps().leadtime),
            moq: articleMoq > variationMoq ? articleMoq : variationMoq
        })
    }

    /* TIENE AFECTACIÓN EN EL DESENROLLADOR */
    getPrice(inputPrice) {
        const props = JSON.parse(JSON.stringify(inputPrice.getProps()))
        props.product.reference = this.getReference(inputPrice.getReference())

        let priceModifier = {
            priceIncrement: this.props.priceIncrement ?? 0,
            priceIncrementDollars: this.props.priceIncrementDollars ?? 0,
            priceIncrementPercent: this.props.priceIncrementPercent ?? 0,
            priceEffectOrder: this.props.priceEffectOrder ?? 9999,
            props: this.props
        }

        if (this.getType() == 'pendantHeight') {
            const delta = this.getValue()?.delta ?? 0
            const usaPrice = [10, 12].includes(inputPrice.getRate())
            priceModifier = {
                ...priceModifier,
                priceIncrement: 0,
                priceIncrementDollars: 0,
                priceIncrementPercent: 0,
            }
            if (delta > 0 && !usaPrice) { // Calculate price increment according to height delta
                priceModifier.priceIncrement = this.props.priceIncrement * delta * Math.max(this.props.segmentCount ?? 1, 1)
            } else if (delta && usaPrice) {
                priceModifier.priceIncrementPercent = this.props.priceIncrementPercent
            }
        }

        return new Price(
            props,
            [
                ...inputPrice.getModifiers(),
                priceModifier
            ]
        )
    }

    timeUntilAvailable(unit: Parameters<typeof convertTimeTo>[1] | 'date' = 'weeks') {
        let result = -1;

        const now = (new Date()).getTime();
        const props = this.getProps();
        const since = props?.availability?.since;
        const until = props?.availability?.until;
        const expected = props?.availability?.expected;

        const hasExpired = until ? protectFromUndefinedOrNaN(until - now, 0) < 0 : false;

        const timeToSince = since ? protectFromUndefinedOrNaN(since - now, 0) : 0;
        const timeToExpected = expected ? protectFromUndefinedOrNaN(expected - now, 0) : 0;

        if (hasExpired) {
            result = -1;
        } else {
            const maxBetweenBoth = Math.max(timeToSince, timeToExpected);

            if (maxBetweenBoth <= 0) {
                result = 0;
            } else {
                if (unit === 'date') {
                    if (timeToExpected === maxBetweenBoth) {
                        result = expected
                    } else if (timeToSince === maxBetweenBoth) {
                        result = since
                    }
                } else {
                    result = Math.ceil(convertTimeTo(maxBetweenBoth, unit));
                }
            }
        }

        return result;
    }

    isAvailable() {
        const now = (new Date()).getTime();
        let available = true
        let since = this.getProps()?.availability?.since
        let until = this.getProps()?.availability?.until
        if (since) {
            available = now >= since
        }
        if (until) {
            available = now < until
        }
        return available
    }

    static getTypes(): variationType[] {
        return ['color', 'temperature', 'control', 'installation', 'pendantHeight']
    }

    static getHexColor(code) {
        return hexDictionary[code];
    }

    static fromERP(row, finishMasters?: Array<{ id: number; code: number }>) {

        const OrdenVariacionValues = {
            A: 1,
            B: 2,
            C: 3,
            D: 4,
            F: 5
        }

        let value: any
        const modifier = parseERPModifier(row);
        if (row.TipVariacion == 'Aca') {
            value = this.getHexColor(modifier)
        } else if (row.TipVariacion == 'Lon') {
            value = {
                min: row.Lonminima,
                standard: row.Lonstandar,
                max: row.Lonmaxima
            }
        }

        if (row.TipVariacion == 'Led') {
            const ledDescription = transformDescription(row.Descustom, row.TipVariacion)
            if (ledDescription) {
                const ledValue = ledDescription.split(' ')?.[0]
                value = ledValue
            }
        }

        row = parseRow(row);

        let catalogId: 16 | 21 = row.Marca === '3VB' ? 16 : 21
        if (Toggles.isEnabled('variationCatalog')) {
            if (!['3VB', '4VT', '2US'].includes(row.Marca)) throw new Error("Not exist catalog value")
            catalogId = (row.Marca === '3VB' || row.Marca === '4VT') ? 16 : row.Marca === '2US' ? 21 : 16
        }

        return new Variation({
            model: row.Modelo,
            referenceBase: row.Articulo,
            catalog: catalogId,
            type: types[row.TipVariacion],
            modifier,
            modifier2: row.Keycus2,
            description: {
                en: transformDescription(row.Descustom, row.TipVariacion),
                es: transformDescription(row.Descustom, row.TipVariacion),
                de: transformDescription(row.Descustom, row.TipVariacion),
                fr: transformDescription(row.Descustom, row.TipVariacion),
                it: transformDescription(row.Descustom, row.TipVariacion),
                us: transformDescription(row.Descustom, row.TipVariacion),

            },
            ral: {
                description: {
                    en: "test ral",
                    es: "test ral",
                    de: "test ral",
                    fr: "test ral",
                    it: "test ral",
                    us: "test ral"
                },
                id: -1
            },
            id: getVariationId(types[row.TipVariacion], modifier, finishMasters),
            value,
            bov: row.BOV,
            moq: row.moqi,
            leadtime: [
                { from: 0, weeks: row.LT_VARSEM },
                { from: row.QTY_JOB1, weeks: row.LT_JOB1 },
                { from: row.QTY_JOB2, weeks: row.LT_JOB2 }
            ],
            priceIncrement: row.Importe,
            priceIncrementDollars: row.ImporteDolar,
            priceIncrementPercent: row.Porcentaje,
            priceEffectOrder: OrdenVariacionValues[row.OrdenVariacion?.trim()] || 9999,
            segmentCount: row.Tramos,
            availability: {
                since: row.VigenciaDesde && new Date(row.VigenciaDesde.split('/').reverse().join('/')).getTime(),
                until: row.VigenciaHasta && new Date(row.VigenciaHasta.split('/').reverse().join('/')).getTime(),
                expected: row.fprevista && new Date(row.fprevista.split('/').reverse().join('/')).getTime()
            },
        })
    }

    modifyFinishData(finishesObject) {
        if (!finishesObject || this.getType() != 'color') return this

        const finishData = finishesObject[this.getModifier()]
        if (finishData) {
            const modifier = this.getModifier()
            const description = finishData.description
            Object.entries(description).forEach(([lang, desc]: [string, string]) => description[lang] = desc.replace(modifier, '').trim())
            this.setDescriptions(description)

            const mastersRal = finishData.ral
            const ral = this.getRal()
            Object.keys(ral.description).forEach((lang) => ral.description[lang] = mastersRal)
            this.setRal(ral)

            const thumbnailFinish = finishData.thumbnailImage
            this.setFinishThumbnail(thumbnailFinish)
        }
        return this
    }

    modifyControlData(controlsObject) {
        if (!controlsObject || this.getType() != 'control') return this
        const controlData = controlsObject[this.getModifier()]

        if (controlData) {
            const description = controlData.description
            this.setDescriptions(description)
        }

        return this
    }

    static fromArticle(article: Article, attribute: variationType) {
        const attributeValue = article?.getAttribute(attribute, false)
        if (!attributeValue) {
            return null
        }
        const ral = article.getRal()
        let value: string | PendantHeightConstraintInterface
        if (attribute == 'color') {
            value = hexDictionary[article.getAttributeModifier(attribute)]
        } else if (attribute == 'pendantHeight') {
            value = {
                min: attributeValue,
                standard: attributeValue,
                max: attributeValue
            }
        }
        return new Variation({
            model: article.getModel(),
            referenceBase: article.getReference(),
            catalog: article.getCatalog(),
            type: attribute,
            modifier: article.getAttributeModifier(attribute),
            modifier2: '',
            description: article.getAttributeDescription(attribute),
            ...(ral && { ral }),
            id: -1,
            value,
            bov: 'B'
        })
    }
}

